export function CommonValuationMistakes() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Common Valuation Mistakes to Avoid
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Many property owners make critical errors when valuing their apartment buildings, leading to unrealistic expectations, failed transactions, or leaving money on the table. Learn from these common mistakes to ensure accurate valuation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-red-600">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Overestimating Based on Potential</h3>
            </div>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Valuing property based on what it "could" generate rather than actual current income. Buyers pay for actual NOI, not hypothetical future income.
            </p>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Solution: Base valuation on current, documented income with realistic expense assumptions</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-red-600">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Using Wrong Cap Rate</h3>
            </div>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Applying cap rates from different markets, property classes, or outdated data. A 1% cap rate error can change value by 15-20%.
            </p>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Solution: Research current cap rates for similar properties in your specific submarket</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-red-600">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Understating Operating Expenses</h3>
            </div>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Forgetting expenses like property taxes, insurance, maintenance reserves, or management fees. This inflates NOI and overvalues the property.
            </p>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Solution: Include all operating expenses; typical expense ratio is 40-60% of effective gross income</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-red-600">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Ignoring Deferred Maintenance</h3>
            </div>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Failing to account for needed repairs and capital expenditures. Buyers will discover these issues and demand price reductions.
            </p>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Solution: Get inspection, identify all needed repairs, and adjust value accordingly</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-red-600">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Emotional Attachment Pricing</h3>
            </div>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Setting price based on personal attachment, money invested, or what you "need" rather than market value. The market doesn't care about your circumstances.
            </p>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Solution: Focus on objective market data and comparable sales, not personal factors</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-red-600">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Using Poor Comparables</h3>
            </div>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Comparing your property to dissimilar buildings in different locations, conditions, or size ranges. This produces inaccurate valuations.
            </p>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Solution: Use only truly comparable properties—similar size, location, condition, and age</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-red-600">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Confusing Gross Income with NOI</h3>
            </div>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Using gross rental income instead of Net Operating Income in cap rate calculations. This dramatically overvalues the property.
            </p>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Solution: Always use NOI (income after all operating expenses) for cap rate valuation</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-red-600">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Ignoring Market Conditions</h3>
            </div>
            <p className="text-gray-700 mb-3 leading-relaxed">
              Using outdated comparable sales or cap rates from different market cycles. Market conditions change rapidly and significantly impact value.
            </p>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Solution: Use only recent sales (past 6-12 months) and current market cap rates</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-lg p-8 lg:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">Best Practices for Accurate Valuation</h3>
          <p className="text-lg text-center mb-10 max-w-3xl mx-auto opacity-90">
            Follow these proven practices to ensure your apartment building valuation is accurate, defensible, and accepted by buyers and lenders:
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-check-double-line text-green-300 text-2xl"></i>
                <h4 className="font-bold text-lg">Use Multiple Methods</h4>
              </div>
              <p className="opacity-90">Apply income, sales comparison, and quick methods. Convergence of values indicates accuracy.</p>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-check-double-line text-green-300 text-2xl"></i>
                <h4 className="font-bold text-lg">Document Everything</h4>
              </div>
              <p className="opacity-90">Keep detailed records of income, expenses, and all assumptions used in your valuation.</p>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-check-double-line text-green-300 text-2xl"></i>
                <h4 className="font-bold text-lg">Be Conservative</h4>
              </div>
              <p className="opacity-90">Use realistic, conservative assumptions. Better to underpromise and overdeliver.</p>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-check-double-line text-green-300 text-2xl"></i>
                <h4 className="font-bold text-lg">Get Professional Input</h4>
              </div>
              <p className="opacity-90">Consult with brokers, appraisers, or investors who know your market intimately.</p>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-check-double-line text-green-300 text-2xl"></i>
                <h4 className="font-bold text-lg">Update Regularly</h4>
              </div>
              <p className="opacity-90">Market conditions change. Update your valuation every 3-6 months if not selling immediately.</p>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-check-double-line text-green-300 text-2xl"></i>
                <h4 className="font-bold text-lg">Test the Market</h4>
              </div>
              <p className="opacity-90">The market ultimately determines value. Buyer feedback provides reality check on pricing.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}